import {useMemo} from "react";
import {create} from "zustand";

import {
  buildBlockingGateQueue,
  selectActiveGateId,
  type BlockingGateDescriptor,
  type BlockingGateId,
} from "@/src/modules/core/lib/compliance-gate-priority";

type BlockingGateState = {
  gates: Partial<Record<BlockingGateId, BlockingGateDescriptor>>;
  /** True while ANY reconcile beat is in flight (refcounted). */
  reconciling: boolean;
  /** Number of overlapping in-flight reconcile beats; `reconciling === reconcileCount > 0`. */
  reconcileCount: number;
};

type BlockingGateActions = {
  registerGate: (descriptor: BlockingGateDescriptor) => void;
  clearGate: (id: BlockingGateId) => void;
  /** `true` enters a reconcile beat, `false` leaves it. Refcounted so overlapping beats (e.g. a
   * gate-resolution reconcile and a focus/autocomplete reconcile) don't clear each other early. */
  setReconciling: (value: boolean) => void;
};

function isSameDescriptor(a: BlockingGateDescriptor | undefined, b: BlockingGateDescriptor) {
  return (
    a != null &&
    a.id === b.id &&
    a.priority === b.priority &&
    a.label === b.label &&
    a.deferrable === b.deferrable
  );
}

export const useBlockingGateStore = create<BlockingGateState & BlockingGateActions>((set) => ({
  gates: {},
  reconciling: false,
  reconcileCount: 0,

  registerGate: (descriptor) =>
    set((state) => {
      if (isSameDescriptor(state.gates[descriptor.id], descriptor)) {
        return state;
      }
      return {gates: {...state.gates, [descriptor.id]: descriptor}};
    }),

  clearGate: (id) =>
    set((state) => {
      if (state.gates[id] == null) {
        return state;
      }
      const next = {...state.gates};
      delete next[id];
      return {gates: next};
    }),

  setReconciling: (value) =>
    set((state) => {
      const reconcileCount = Math.max(0, state.reconcileCount + (value ? 1 : -1));
      const reconciling = reconcileCount > 0;
      if (reconcileCount === state.reconcileCount && reconciling === state.reconciling) {
        return state;
      }
      return {reconcileCount, reconciling};
    }),
}));

/** Id of the single gate that should be visible, by priority. */
export function useActiveBlockingGateId(): BlockingGateId | null {
  return useBlockingGateStore((state) => selectActiveGateId(state.gates));
}

/** Priority-sorted descriptors, for the sticky bar. */
export function useBlockingGateQueue(): BlockingGateDescriptor[] {
  const gates = useBlockingGateStore((state) => state.gates);
  return useMemo(() => buildBlockingGateQueue(gates), [gates]);
}

export function useBlockingGateReconciling(): boolean {
  return useBlockingGateStore((state) => state.reconciling);
}

export function useRegisterBlockingGate() {
  return useBlockingGateStore((state) => state.registerGate);
}

export function useClearBlockingGate() {
  return useBlockingGateStore((state) => state.clearGate);
}

export function useSetBlockingGateReconciling() {
  return useBlockingGateStore((state) => state.setReconciling);
}
